import React from "react";
import { normalizeExternalUrl } from "../../Helper/urls";

/**
 * Extensible Organization Action Bar (A-005.7).
 * Launch actions: Visit Website + optional Donate.
 * Pass `extraActions` for future Share / Follow / Volunteer / Events / etc.
 *
 * Action shape: { key, href|url, label, ariaLabel, icon?, className? }
 */
function OrganizationActionBar({
  organizationName = "Organization",
  website,
  donationUrl,
  extraActions = [],
}) {
  const actions = [];

  if (website) {
    actions.push({
      key: "website",
      href: normalizeExternalUrl(website),
      label: "Visit Website",
      ariaLabel: `Visit ${organizationName} website`,
      icon: "fas fa-external-link-alt",
      className: "ae-btn ae-btn--accent ae-btn--entity-website org-action-btn",
    });
  }

  if (donationUrl) {
    actions.push({
      key: "donate",
      href: normalizeExternalUrl(donationUrl),
      label: "Donate",
      ariaLabel: `Donate to ${organizationName}`,
      icon: "fas fa-heart",
      className: "ae-btn ae-btn--primary ae-btn--entity-donate org-action-btn org-action-btn--donate",
    });
  }

  (Array.isArray(extraActions) ? extraActions : []).forEach((action) => {
    if (!action || !action.key) {
      return;
    }
    const rawHref = action.href || action.url || action.to || "";
    const isInternal =
      typeof rawHref === "string" &&
      rawHref.charAt(0) === "/" &&
      rawHref.charAt(1) !== "/";
    const href = isInternal
      ? rawHref
      : action.href || (action.url ? normalizeExternalUrl(action.url) : "");
    if (!href || !action.label) {
      return;
    }
    actions.push({
      key: action.key,
      href,
      label: action.label,
      ariaLabel: action.ariaLabel || action.label,
      icon: action.icon,
      className: action.className || "ae-btn ae-btn--secondary org-action-btn",
      target:
        action.target !== undefined
          ? action.target
          : isInternal
          ? "_self"
          : "_blank",
      rel:
        action.rel !== undefined
          ? action.rel
          : isInternal
          ? undefined
          : "noopener noreferrer",
    });
  });

  if (!actions.length) {
    return null;
  }

  return (
    <div
      className="org-action-bar"
      role="group"
      aria-label={`${organizationName} actions`}
    >
      {actions.map((action) => (
        <a
          key={`org-action-${action.key}`}
          href={action.href}
          className={action.className}
          aria-label={action.ariaLabel}
          target={action.target !== undefined ? action.target : "_blank"}
          rel={action.rel !== undefined ? action.rel : "noopener noreferrer"}
        >
          {action.icon ? (
            <i className={`${action.icon} org-action-icon`} aria-hidden="true" />
          ) : null}
          <span>{action.label}</span>
        </a>
      ))}
    </div>
  );
}

export default OrganizationActionBar;
